/** @typedef {import('headlight-configurable-application').Pict} Pict */

const MaterialsCascadePicker = require('./PictProvider-InputType-MaterialsCascadePicker.js');
const ScopedEntityData = require('./_ScopedEntityData.js');
const libFilterToggleUI = require('./_FilterToggleUI.js');

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'MaterialsSamplePicker',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
	// Opt into the app save-data lifecycle so onBeforeSaveDataAsync (the SampleSequencing stamp, run
	// before the _EntityData projection sweep) actually fires — legacy parity (the MES Sample selector
	// set AutoSaveDataWithApp too).
	AutoSaveDataWithApp: true,
	// The sample selector creates samples by default; a field opts out via PictForm.AllowEntityCreation.
	AllowEntityCreation: true,
	Entity: 'Sample',
	// How long the first save waits for the device position of a sample created on this form.
	CreationPositionSaveWaitMS: 5000,
};

/**
 * The capture coordinates in a position from the GeoProvider, or null when it holds no usable fix.
 * @param {any} pPosition - `{ CaptureLatitude, CaptureLongitude }` from GeoProvider.getGeoCoords, or null
 * @return {{ CaptureLatitude: number, CaptureLongitude: number }|null}
 */
function readCapturePosition(pPosition)
{
	if (!pPosition || typeof pPosition !== 'object')
	{
		return null;
	}
	const tmpLatitude = Number(pPosition.CaptureLatitude);
	const tmpLongitude = Number(pPosition.CaptureLongitude);
	if (!Number.isFinite(tmpLatitude) || !Number.isFinite(tmpLongitude) || (tmpLatitude === 0 && tmpLongitude === 0))
	{
		return null;
	}
	return { CaptureLatitude: tmpLatitude, CaptureLongitude: tmpLongitude };
}

/**
 * Whether a sample record still needs its creating device's position: a sample the server does not have
 * yet, with no capture coordinates of its own (a seeded or configured record may bring them).
 * @param {any} pRecord
 * @return {boolean}
 */
function needsCapturePosition(pRecord)
{
	if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord) || !pRecord.GUIDSample)
	{
		return false;
	}
	if (Number(pRecord.IDSample) > 0)
	{
		return false;
	}
	return !readCapturePosition(pRecord);
}

/**
 * The Sample picker (beast B2 — THE HUB): the Picker-stack port of MaterialsSampleSelector, mirroring
 * the legacy provider 1:1 on the MaterialsCascadePicker base. The sample owns the family's tracking
 * identity; its cascade drives the DocumentPolyJoin synthesis, the spec-year scope, the auto-generated
 * Sample Name, and the form's data requirements; its save hook stamps naming/numbering before the
 * _EntityData projection sweep. Creation (type-to-create) synthesizes a QR-coded, project-bound record.
 */
class PictProviderInputTypeMaterialsSamplePicker extends MaterialsCascadePicker
{
	/**
	 * @param {Object} pFable @param {Object} [pOptions] @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DEFAULT_CONFIGURATION, pOptions), pServiceHash);
		/** @type {Pict} */
		this.pict;
	}

	/**
	 * A sample is ALWAYS tracked by GUIDSample (legacy hard override — even with creation configured
	 * off, the value identity stays the GUID).
	 * @param {Record<string, any>} pInput @return {string}
	 */
	_getRecordIdentity(pInput)
	{
		return pInput?.PictForm?.RecordIdentity || 'GUIDSample';
	}

	/**
	 * The synthesized sample: the base mint (document GUID + stanza + salt), then the sample-specific
	 * enrichment — project binding, creating user, designated time, and a fresh QR code. Ported 1:1
	 * (including the legacy '-S-' fallback for a GenerateGUID-opted-out record).
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex]
	 * @return {Record<string, any>}
	 */
	_getDefaultRecord(pView, pInput, pRowIndex)
	{
		let tmpRecord = super._getDefaultRecord(pView, pInput, pRowIndex);
		if (!tmpRecord)
		{
			tmpRecord = { GUIDSample: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-S-${this._getSalt(pView, pInput, pRowIndex)}` };
		}
		if (this.pict.AppData.DocumentData?.ReportData?.IDProject > 0)
		{
			tmpRecord.IDProject = this.pict.AppData.DocumentData.ReportData.IDProject;
		}
		tmpRecord.CreatingIDUser = this.pict.AppData.CurrentUser?.UserID;
		tmpRecord.DesignatedTime = new Date().toISOString();
		tmpRecord.QRCode = this.pict.providers.QRCodeProvider.generateNewQRCode(tmpRecord);
		return tmpRecord;
	}

	/**
	 * Create the sample (base), then ask for the creating device's position so the new sample carries
	 * where it was taken (Sample.CaptureLatitude/CaptureLongitude, copied onto its "Sample created" log).
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue
	 * @param {string} [pTransactionGUID] @param {number|string|null} [pRowIndex]
	 * @return {any} the synthesized identity value (or null when nothing synthesized)
	 */
	_handleDefaultRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex)
	{
		const tmpValue = super._handleDefaultRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex);
		if (tmpValue)
		{
			this._captureCreationPosition(pView, pInput, pRowIndex);
		}
		return tmpValue;
	}

	/**
	 * The creation-time position lookups still waiting for an answer, by GUIDSample.
	 * @return {Map<string, Promise<void>>}
	 */
	_creationPositionLookups()
	{
		if (!this._pendingCreationPositions)
		{
			/** @type {Map<string, Promise<void>>} */
			this._pendingCreationPositions = new Map();
		}
		return this._pendingCreationPositions;
	}

	/**
	 * Ask the GeoProvider (the iPad's native location, else the browser's, which may prompt) where the
	 * device is, and stamp the answer on the sample just created. Only an interactive form asks, and only
	 * for a sample with no server row and no coordinates of its own. A refusal, an error or no answer
	 * leaves the coordinates unset (0 on the server). The device answers offline too, so an iPad saving
	 * offline keeps the position in the document until it syncs.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex]
	 */
	_captureCreationPosition(pView, pInput, pRowIndex)
	{
		const tmpGeoProvider = this.pict.providers?.GeoProvider;
		if (!this.pict.AppData?.Interactive || !tmpGeoProvider || typeof tmpGeoProvider.getGeoCoords !== 'function')
		{
			return;
		}
		const tmpRecordAddress = this._getRecordAddress(pInput, pRowIndex);
		const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
		if (!needsCapturePosition(tmpRecord))
		{
			return;
		}
		const tmpGUIDSample = tmpRecord.GUIDSample;
		const tmpLookups = this._creationPositionLookups();
		/** @type {Promise<void>} */
		const tmpLookup = Promise.resolve()
			.then(() => tmpGeoProvider.getGeoCoords())
			.catch((pError) =>
			{
				this.pict.log.info(`No device position for new sample ${tmpGUIDSample}: ${pError?.message || pError}`);
				return null;
			})
			.then((pPosition) =>
			{
				// A save closes the lookup: a late answer must not change a sample that was already saved.
				if (tmpLookups.get(tmpGUIDSample) !== tmpLookup)
				{
					return;
				}
				tmpLookups.delete(tmpGUIDSample);
				this._stampCapturePosition(pView, tmpRecordAddress, tmpGUIDSample, pPosition);
			});
		tmpLookups.set(tmpGUIDSample, tmpLookup);
	}

	/**
	 * Put the device position on the held sample, if it is still the sample the lookup was for and it has
	 * not gained a server row or coordinates in the meantime.
	 * @param {Object} pView @param {string} pRecordAddress @param {string} pGUIDSample @param {any} pPosition
	 */
	_stampCapturePosition(pView, pRecordAddress, pGUIDSample, pPosition)
	{
		const tmpPosition = readCapturePosition(pPosition);
		if (!tmpPosition)
		{
			return;
		}
		const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), pRecordAddress);
		if (!needsCapturePosition(tmpRecord) || tmpRecord.GUIDSample !== pGUIDSample)
		{
			return;
		}
		tmpRecord.CaptureLatitude = tmpPosition.CaptureLatitude;
		tmpRecord.CaptureLongitude = tmpPosition.CaptureLongitude;
	}

	/**
	 * Let the creation-time position lookups land before the save projects the sample, waiting at most
	 * `CreationPositionSaveWaitMS`, then close them so nothing a later answer brings reaches a later save.
	 * @return {Promise<void>}
	 */
	_settleCreationPositions()
	{
		const tmpLookups = this._creationPositionLookups();
		if (tmpLookups.size < 1)
		{
			return Promise.resolve();
		}
		const tmpWaitMS = Number(this.options?.CreationPositionSaveWaitMS);
		/** @type {ReturnType<typeof setTimeout>|null} */
		let tmpTimer = null;
		const tmpTimeout = new Promise((fResolve) =>
		{
			tmpTimer = setTimeout(fResolve, Number.isFinite(tmpWaitMS) ? tmpWaitMS : _DEFAULT_CONFIGURATION.CreationPositionSaveWaitMS);
		});
		return Promise.race([ Promise.all(Array.from(tmpLookups.values())), tmpTimeout ]).then(() =>
		{
			clearTimeout(tmpTimer);
			tmpLookups.clear();
		});
	}

	/**
	 * Init: default the QRCode EntityTag, wire the external Sample Search Panel, and mint/bind the
	 * family tracking GUID — the sample OWNS the tracking identity the sibling selectors bind from.
	 * Ported 1:1; the search-panel selection lands through the broker value-commit primitive instead
	 * of the legacy select2 DOM pokes.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'QRCode';
		}
		this._applyDisabledOnceSaved(pInput, pValue);
		this._applySampleLock(pInput, pValue);
		this._wireSampleSearchPanel(pView, pInput, null);
		// sample is in charge of the family for tracking
		if (pInput.PictForm.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
		{
			this._setTrackingGUID(pInput, null, pInput.PictForm.SampleTrackingGUID);
		}
		else if (!this._getTrackingGUID(pInput))
		{
			const tmpTrackingGUID = this.pict.getUUID();
			this._setTrackingGUID(pInput, null, tmpTrackingGUID);
			pInput.PictForm.SampleTrackingGUID = tmpTrackingGUID;
		}
		const tmpResult = super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		this._paintSampleLock(pInput);
		return tmpResult;
	}

	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'QRCode';
		}
		this._applyDisabledOnceSaved(pInput, pValue);
		if (pInput.PictForm.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
		{
			this._setTrackingGUID(pInput, pRowIndex, pInput.PictForm.SampleTrackingGUID);
			return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		}
		let tmpTrackingGUID = this._getTrackingGUID(pInput, pRowIndex);
		if (!tmpTrackingGUID)
		{
			tmpTrackingGUID = this.pict.getUUID();
			this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
			pInput.PictForm.SampleTrackingGUID = true; // different rows will have different values so avoid confusion
		}
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Wire the external Sample Search Panel to THIS picker: a panel selection stashes the record and
	 * lands it through the value-commit primitive. The panel's row is a fresh server read, so it is
	 * reconciled with the held record like an open refresh (a re-pick of the held sample keeps an
	 * unsaved edit to it) and pre-written to `__Record`, and the value is marked read so the fenced
	 * refresh short-circuits on it (no re-fetch); committing the GUID then runs the cascade + widget
	 * repaint on the standard path. (The legacy FIXME about the last-initialized selector winning still
	 * applies — one panel callback slot.)
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 */
	_wireSampleSearchPanel(pView, pInput, pRowIndex)
	{
		const tmpPanel = this.pict.views.HeadlightPaneSamples;
		if (!tmpPanel || typeof tmpPanel.setSampleSelectionCallback !== 'function')
		{
			return;
		}
		tmpPanel.setSampleSelectionCallback((pSample) =>
		{
			if (pInput.PictForm && pInput.PictForm.Disabled)
			{
				this.pict.log.info(`Sample search panel pick ignored: input ${pInput.Macro.RawHTMLID} is disabled.`);
				return;
			}
			this.pict.log.info(`Sample selected from the search panel: ${pSample?.GUIDSample} -> input ${pInput.Macro.RawHTMLID}`);
			this.pict.AppData.SelectedSample = pSample;
			const tmpIdentity = this._getRecordIdentity(pInput);
			const tmpValue = pSample[tmpIdentity];
			const tmpRecordAddress = this._getRecordAddress(pInput, pRowIndex);
			const tmpHeld = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress,
				this._reconcileRefreshedRecord(pView, pInput, tmpHeld, pSample, tmpIdentity, tmpValue, pRowIndex));
			this._openRefreshes().add(this._openRefreshKey(pInput, tmpValue, pRowIndex));
			this._setValueAndCommit(pView, pInput, tmpValue, pRowIndex);
		});
	}

	/**
	 * Opt-in via PictForm.DisabledOnceSaved: disable this selector once it has a value AND the document has
	 * been persisted, so a saved selection can't be swapped; a brand-new (unsaved) form stays editable. Sets
	 * PictForm.Disabled before the picker mounts. Ported 1:1 from `MaterialsSampleSelector._applyDisabledOnceSaved`.
	 * @param {Record<string, any>} pInput @param {any} pValue - the selected sample's GUID, when one exists.
	 */
	_applyDisabledOnceSaved(pInput, pValue)
	{
		if (!pInput || !pInput.PictForm || !pInput.PictForm.DisabledOnceSaved)
		{
			return;
		}
		const tmpDocumentSaved = this.pict?.AppData?.DocumentData?.ReportData?.IDDocument > 0;
		if (pValue && tmpDocumentSaved)
		{
			pInput.PictForm.Disabled = true;
		}
	}

	/**
	 * The lock entries, keyed by document + input, so a second document opened in the same app starts locked again.
	 * @return {Map<string, { Locked: boolean, Input: Record<string, any> }>}
	 */
	_sampleLocks()
	{
		if (!this._sampleLockEntries)
		{
			this._sampleLockEntries = new Map();
		}
		return this._sampleLockEntries;
	}

	/** @param {string} pRawHTMLID @return {string} */
	_sampleLockKey(pRawHTMLID)
	{
		return `${this.pict?.AppData?.DocumentData?.ReportData?.GUIDDocument || ''}::${pRawHTMLID}`;
	}

	/**
	 * A visible Sample selector opens locked when it already holds a sample, so a stray click can't swap the
	 * sample and re-fill the fields that follow it; the lock button unlocks it for this session. An empty field
	 * opens unlocked. A field the manifest (or DisabledOnceSaved) already disables gets no lock, and
	 * `PictForm.LockUntilUnlocked: false` turns the lock off. Decided once per document, so a re-render keeps
	 * the user's choice.
	 * @param {Record<string, any>} pInput @param {any} pValue - the selected sample's GUID, when one exists.
	 */
	_applySampleLock(pInput, pValue)
	{
		if (!pInput || !pInput.PictForm || !pInput.Macro || pInput.PictForm.InputType !== 'MaterialsSampleSelector' || pInput.PictForm.LockUntilUnlocked === false)
		{
			return;
		}
		const tmpKey = this._sampleLockKey(pInput.Macro.RawHTMLID);
		let tmpEntry = this._sampleLocks().get(tmpKey);
		if (!tmpEntry)
		{
			if (pInput.PictForm.Disabled)
			{
				return;
			}
			tmpEntry = { Locked: !!pValue, Input: pInput };
			this._sampleLocks().set(tmpKey, tmpEntry);
		}
		tmpEntry.Input = pInput;
		if (tmpEntry.Locked)
		{
			pInput.PictForm.Disabled = true;
		}
	}

	/**
	 * Flip a Sample selector between locked and unlocked (the lock button's click handler).
	 * @param {string} pRawHTMLID
	 * @return {boolean} whether the field is now locked.
	 */
	toggleSampleLock(pRawHTMLID)
	{
		const tmpEntry = this._sampleLocks().get(this._sampleLockKey(pRawHTMLID));
		if (!tmpEntry)
		{
			return false;
		}
		tmpEntry.Locked = !tmpEntry.Locked;
		if (tmpEntry.Locked)
		{
			tmpEntry.Input.PictForm.Disabled = true;
		}
		else
		{
			delete tmpEntry.Input.PictForm.Disabled;
		}
		// The host id helper lives on the (untyped-to-tsc) pict-section-picker base.
		this._reflectDisabledState(tmpEntry.Input, /** @type {any} */ (this).getPickerHostID(pRawHTMLID));
		libFilterToggleUI.syncToggleDisabledState(this.pict, this.getSelectFilterToggleID(pRawHTMLID), libFilterToggleUI.isInputDisabled(tmpEntry.Input));
		this._paintSampleLock(tmpEntry.Input);
		return tmpEntry.Locked;
	}

	/**
	 * Paint the lock button and the Sample Search Panel button from the lock state. The button stays hidden on a
	 * field without a lock.
	 * @param {Record<string, any>} pInput
	 */
	_paintSampleLock(pInput)
	{
		const tmpRawHTMLID = pInput?.Macro?.RawHTMLID;
		const tmpEntry = tmpRawHTMLID ? this._sampleLocks().get(this._sampleLockKey(tmpRawHTMLID)) : null;
		if (!tmpEntry)
		{
			return;
		}
		const tmpControl = this._firstElement(`#SAMPLE-LOCK-CONTROL-${tmpRawHTMLID}`);
		const tmpButton = this._firstElement(`#SAMPLE-LOCK-${tmpRawHTMLID}`);
		const tmpPanelButton = this._firstElement(`#SAMPLE-PANEL-BUTTON-${tmpRawHTMLID}`);
		if (tmpControl && tmpControl.classList)
		{
			tmpControl.classList.remove('is-hidden');
		}
		if (tmpButton)
		{
			const tmpTitle = tmpEntry.Locked ? 'Locked so a stray click can\'t change the sample. Click to unlock.' : 'Click to lock the sample.';
			tmpButton.setAttribute('title', tmpTitle);
			tmpButton.setAttribute('aria-label', tmpTitle);
			tmpButton.setAttribute('aria-pressed', tmpEntry.Locked ? 'true' : 'false');
			const tmpIcon = tmpButton.querySelector ? tmpButton.querySelector('i') : null;
			if (tmpIcon)
			{
				tmpIcon.className = tmpEntry.Locked ? 'fas fa-lock' : 'fas fa-lock-open';
			}
		}
		if (tmpPanelButton)
		{
			tmpPanelButton.disabled = !!pInput.PictForm.Disabled;
		}
	}

	/**
	 * Save-lifecycle hook: let a new sample's device position land, then stamp naming + numbering (Name,
	 * LabControlNumber, Sequenced) on the Sample record, all BEFORE the base projects it into _EntityData
	 * (super), so the projected copy carries them. Delegates the naming to SampleSequencing; idempotent and
	 * fail-safe.
	 * @param {(pError?: Error) => void} fCallback
	 */
	onBeforeSaveDataAsync(fCallback)
	{
		this._settleCreationPositions().then(() =>
		{
			const tmpProvider = this.pict.providers?.SampleSequencing;
			if (!tmpProvider || typeof tmpProvider.applyBeforeSave !== 'function')
			{
				return super.onBeforeSaveDataAsync(fCallback);
			}
			tmpProvider.applyBeforeSave().then(() => super.onBeforeSaveDataAsync(fCallback)).catch((pError) =>
			{
				this.pict.log.error(`SampleSequencing stamp failed before save (continuing): ${pError?.message || pError}`);
				super.onBeforeSaveDataAsync(fCallback);
			});
		});
	}

	/**
	 * The Sample cascade — the port of the legacy `_onRecordRefresh`, fired from the seam after
	 * `__Record` lands (select, create, seed and clear paths):
	 *   1. synthesize/maintain the sibling DocumentPolyJoin (document linkage; template-minted GUID,
	 *      saved-vs-unsaved document key, IDRemote or the GUIDSample+_Dest_GUIDSample redirect);
	 *   2. repaint dependents in-section (marshalToView);
	 *   3. super — the family event bus (SampleChanged:<trackingGUID>:<address> + trigger group);
	 *   4. refresh the spec-year scope from the sample's project (resolver dedupes, fail-open);
	 *   5. live-refresh the auto-generated Sample Name (never clobbers a manual edit);
	 *   6. re-apply the form's data requirements for the new sample.
	 * @param {Object} pView @param {Record<string, any>} pInput
	 * @param {Record<string, any>|Array<Record<string, any>>} pRecord
	 * @param {number|string} [pRowIndex] @param {string} [pTransactionGUID]
	 */
	_onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID)
	{
		const tmpSampleAddress = this._getRecordAddress(pInput, pRowIndex);
		/** @type {Record<string, any>|null} */
		let tmpSample = (pRecord && typeof pRecord === 'object' && !Array.isArray(pRecord)) ? pRecord : null;
		if (!tmpSample)
		{
			tmpSample = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleAddress);
		}
		if (tmpSample && typeof tmpSample === 'object' && Object.keys(tmpSample).length === 0)
		{
			// The seam hands a cleared picker `{}`; the legacy refresh handed a falsy record. Normalize so
			// the DocumentPolyJoin branch below sees "no sample", exactly like the legacy did.
			tmpSample = null;
		}
		const tmpSampleDocumentPolyJoinAddress = pInput.PictForm.DocumentPolyJoinAddress || `${tmpSampleAddress}_DocumentPolyJoin`;
		let tmpDocumentPolyJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleDocumentPolyJoinAddress);
		if (ScopedEntityData.referencesSample(tmpSample))
		{
			// A held join with no RemoteType is a stub left by a clear; rebuild it rather than stamp it.
			if (!tmpDocumentPolyJoin || typeof tmpDocumentPolyJoin !== 'object' || !tmpDocumentPolyJoin.RemoteType)
			{
				tmpDocumentPolyJoin = JSON.parse(JSON.stringify(pInput.PictForm.DefaultDocumentPolyJoinRecord ||
					{
						GUIDDocumentPolyJoin: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-S_DPJ-${this._getSalt(pView, pInput, pRowIndex)}`,
						RemoteType: 'Sample',
						IDDocument: `{~D:AppData.DocumentData.ReportData.IDDocument~}`,
						GUIDDocument: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}`,
						IDRemote: tmpSample.IDSample,
					}));
				if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object')
				{
					this._translateRecordTemplateFields(tmpDocumentPolyJoin);
				}
			}
			if (!tmpDocumentPolyJoin.GUIDDocumentPolyJoin)
			{
				tmpDocumentPolyJoin.GUIDDocumentPolyJoin = this.pict.getUUID();
			}
			const tmpDocument = this.pict.AppData.DocumentData.ReportData;
			if (tmpDocument)
			{
				if (tmpDocument.IDDocument > 0)
				{
					tmpDocumentPolyJoin.IDDocument = tmpDocument.IDDocument;
					delete tmpDocumentPolyJoin.GUIDDocument;
				}
				else
				{
					tmpDocumentPolyJoin.GUIDDocument = tmpDocument.GUIDDocument;
					delete tmpDocumentPolyJoin.IDDocument;
				}
			}
			if (!tmpDocumentPolyJoin.IDRemote)
			{
				if (tmpSample.IDSample)
				{
					tmpDocumentPolyJoin.IDRemote = tmpSample.IDSample;
				}
				else
				{
					tmpDocumentPolyJoin.GUIDSample = tmpSample.GUIDSample;
					tmpDocumentPolyJoin._Dest_GUIDSample = 'IDRemote';
					delete tmpDocumentPolyJoin.IDRemote;
				}
			}
		}
		else if (tmpDocumentPolyJoin && !tmpSample)
		{
			tmpDocumentPolyJoin = { };
		}
		//NOTE: need to set this here in case it is used in the TestInstanceSampleJoin default record template
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleDocumentPolyJoinAddress, tmpDocumentPolyJoin);
		// ensures any fields that use this record are updated immediately, if they are in the same section
		if (typeof pView.marshalToView === 'function')
		{
			pView.marshalToView();
		}
		super._onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID);
		// Refresh spec-year scope from the sample's project. Resolver dedupes when the project hasn't
		// changed; fail-open paths inside the resolver handle bare contracts / empty stub sets / no
		// spec infra. Triggered here because providers don't receive the form's global event channel.
		this.pict.providers.SpecScopeResolver?.loadForSampleRecord?.(tmpSample);
		// Live-refresh the auto-generated Sample Name from the current source fields — only while the
		// user hasn't edited the (now editable) Name field, so a manual edit is never clobbered.
		if (this.pict.providers.SampleSequencing?.refreshSampleName?.())
		{
			if (typeof pView.marshalToView === 'function')
			{
				pView.marshalToView();
			}
		}
		this.pict.providers.DataRequirementsProvider.applyDataRequirements(this.pict.views.PictFormMetacontroller.manifestDescription, pRecord).catch((pError) =>
		{
			this.pict.log.error(`Error applying data requirements after sample refresh: ${pError.message || pError}`);
		});
	}

	/**
	 * Family + save-sweep events — ported 1:1 from the legacy `_handleEvent`:
	 *   - ProjectEntityDataRecords: project the sample + its DocumentPolyJoin into the sweep target
	 *     (the base sweep handler re-runs idempotently through super — same target, same GUID keys);
	 *   - targeted (tracking-GUID-matched): SampleStatusChanged stamps the status onto the record;
	 *     PushRecord copies the record to the requested address.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue @param {string} pHTMLSelector
	 * @param {number|string|null} pRowIndex @param {string} pEvent @param {string} pTransactionGUID
	 */
	_handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		if (!pEvent || typeof pEvent !== 'string')
		{
			return;
		}

		const [ tmpEventType, tmpTrackingGUID, tmpEventPayload ] = pEvent.split(':');

		if (tmpEventType === 'ProjectEntityDataRecords')
		{
			const tmpTarget = this._getProjectionTarget(pInput, pEvent);
			if (!tmpTarget)
			{
				return;
			}
			this._syncRecordToEntityData(pView, pInput, pRowIndex, pTransactionGUID, tmpTarget);
			const tmpSampleAddress = this._getRecordAddress(pInput, pRowIndex);
			const tmpDocumentPolyJoinAddress = pInput.PictForm.DocumentPolyJoinAddress || `${tmpSampleAddress}_DocumentPolyJoin`;
			const tmpDocumentPolyJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpDocumentPolyJoinAddress);
			if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object' && !Array.isArray(tmpDocumentPolyJoin) && tmpDocumentPolyJoin.GUIDDocumentPolyJoin)
			{
				if (!tmpTarget.DocumentPolyJoin || typeof tmpTarget.DocumentPolyJoin !== 'object' || Array.isArray(tmpTarget.DocumentPolyJoin))
				{
					tmpTarget.DocumentPolyJoin = { };
				}
				tmpTarget.DocumentPolyJoin[tmpDocumentPolyJoin.GUIDDocumentPolyJoin] = tmpDocumentPolyJoin;
			}

			return;
		}
		// global events above here; below are targetted events
		if (tmpTrackingGUID !== this._getTrackingGUID(pInput, pRowIndex))
		{
			return;
		}
		if (tmpEventType === 'SampleStatusChanged')
		{
			const tmpRecordAddress = this._getRecordAddress(pInput, pRowIndex);
			const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
			tmpRecord.Status = tmpEventPayload;
			return;
		}

		if (tmpEventType === 'PushRecord')
		{
			const tmpSampleAddress = this._getRecordAddress(pInput, pRowIndex);
			const tmpSampleRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleAddress);
			this.pict.manifest.setValueAtAddress(this.pict, tmpEventPayload, tmpSampleRecord);
		}
	}
}

module.exports = PictProviderInputTypeMaterialsSamplePicker;

module.exports.default_configuration = _DEFAULT_CONFIGURATION;
